import { type Registry as Services, service } from '@ember/service';
import Model, { attr, type HasMany, hasMany } from '@ember-data/model';
import { Type } from '@warp-drive/core-types/symbols';
import dayjs from 'dayjs';
import { ISO_DAYS } from 'frontend-utils/utils/constants/constant-values';
import type { Rssubtype } from 'lumen/models';
import type { MealTypeValues } from 'lumen/utils/constants/general-constants';
import {
  TYPE_ID_BEST_FLEX,
  TYPE_ID_DOW,
  TYPE_ID_LEADTIME,
  TYPE_ID_LOS,
  TYPE_ID_MEALTYPE,
  TYPE_ID_MEMBERSHIP,
  TYPE_ID_MOBILE,
  TYPE_ID_NON_REF,
  TYPE_ID_POS,
  TYPE_ID_ROOMTYPE,
  TYPE_ID_SEMI_FLEX,
  TYPE_ID_TO_TRANSLATION_MAPPING,
} from 'lumen/utils/constants/rate-strategy-constants';
import {
  sortMealtypeIds,
  sortMembershipTypeIds,
  sortRoomtypeIds,
} from 'lumen/utils/sort-ids';

export default class Rstype extends Model {
  [Type] = 'rstype' as const;

  // OpenAPI spec: https://git.mylighthouse.com/OTA-Insight/pricing-insight/-/blob/master/api/openapi-spec/openapi.yaml
  // Rate strategy type corresponds with one "block" in the Strategy page
  // Don't use this property directly, use filteredSubtypes instead
  @hasMany('rssubtype', { async: false, inverse: 'rstype' })
  declare rssubtypes: HasMany<Rssubtype>;

  @attr('string') declare message: string;
  @attr('string') declare name: keyof typeof TYPE_ID_TO_TRANSLATION_MAPPING;

  @service declare intl: Services['intl'];

  // 1. Filter out missing rssubtypes
  // 2. Sort rssubtypes to make sure they always appear in the same order in the summary and detail view
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get filteredSubtypes() {
    // Filter out missing rssubtypes (e.g mobile, mealtype, member rates)
    // For POS and Mobile we do want to show missing rssubtypes (https://otainsight.slack.com/archives/G01BUCJAFCN/p1639484239295700)
    const availableSubtypes =
      this.isPosType || this.isMobileType
        ? [...this.rssubtypes]
        : [...this.rssubtypes].filter((subtype) => !subtype.isMissing);

    if (this.isDowType) {
      const sortedDays = availableSubtypes.sort((a, b) =>
        a.label?.localeCompare(b.label),
      );

      // Check if we should put sunday first (for US users)
      if (dayjs.localeData().firstDayOfWeek() === ISO_DAYS.sun) {
        // First pop off sunday
        const sunday = sortedDays.pop();
        // Then add it to the front of the array
        if (sunday) {
          sortedDays.unshift(sunday);
        }
      }

      return sortedDays;
    }

    if (this.isLeadtimeType) {
      return availableSubtypes.sort((leadtimeA, leadtimeB) => {
        const parsedLabelA = parseInt(leadtimeA.label, 10);
        const parsedLabelB = parseInt(leadtimeB.label, 10);
        return parsedLabelB - parsedLabelA;
      });
    }

    if (this.isLosType) {
      return availableSubtypes.sort((a, b) => a.label?.localeCompare(b.label));
    }

    if (this.isMobileType) {
      // We filter out all platforms that only contain missing data
      return availableSubtypes
        .sort(
          (platformA, platformB) =>
            Number(platformB.isMobileBookingSubtype) -
            Number(platformA.isMobileBookingSubtype),
        ) // First make sure the OTA order is correct (Booking always before Expedia)
        .sort((platformA, platformB) =>
          platformA.label.localeCompare(platformB.label),
        ); // Then sort on platform name (Mobile always before Application)
    }

    if (this.isMealtypeType) {
      // We filter out all mealtypes that only contain missing data
      // https://otainsight.slack.com/archives/G01BUCJAFCN/p1618992510211500
      // Another exception for mealtypes: we don't want to show Room Only in the summary view etc
      return availableSubtypes
        .filter(
          (subtype) =>
            !subtype.isNotOffered && !subtype.isMealtypeRoomOnlySubtype,
        )
        .sort((mealtypeA, mealtypeB) =>
          sortMealtypeIds(
            parseInt(mealtypeA.label, 10) as MealTypeValues,
            parseInt(mealtypeB.label, 10) as MealTypeValues,
          ),
        );
    }

    if (this.isRoomtypeType) {
      return availableSubtypes.sort((roomtypeA, roomtypeB) =>
        sortRoomtypeIds(roomtypeA.label, roomtypeB.label),
      );
    }

    if (this.isPosType) {
      return availableSubtypes
        .sort(
          (posA, posB) =>
            Number(posB.isPosBookingSubtype) - Number(posA.isPosBookingSubtype),
        ) // First make sure the OTA order is correct (Booking always before Expedia)
        .sort((posA, posB) => posA.label.localeCompare(posB.label)); // Then sort on country name
    }

    if (this.isMemberRatesType) {
      return availableSubtypes.sort((membershipA, membershipB) =>
        sortMembershipTypeIds(membershipA.name, membershipB.name),
      );
    }

    return availableSubtypes;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedTypeName() {
    return this.intl.t(TYPE_ID_TO_TRANSLATION_MAPPING[this.name]).toString();
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get icon() {
    if (this.isBestFlexType) {
      return 'rates-best-flex';
    }

    if (this.isSemiFlexType) {
      return 'rates-semi-flex';
    }

    return null;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isBestFlexType() {
    return this.name === TYPE_ID_BEST_FLEX;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isDowType() {
    return this.name === TYPE_ID_DOW;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isLeadtimeType() {
    return this.name === TYPE_ID_LEADTIME;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isLosType() {
    return this.name === TYPE_ID_LOS;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isMealtypeType() {
    return this.name === TYPE_ID_MEALTYPE;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isMemberRatesType() {
    return this.name === TYPE_ID_MEMBERSHIP;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isMissing() {
    return this.message === 'general.missing';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isMobileType() {
    return this.name === TYPE_ID_MOBILE;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isNonRefType() {
    return this.name === TYPE_ID_NON_REF;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isNotOffered() {
    return this.message === 'rates.notoffered';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isPosType() {
    return this.name === TYPE_ID_POS;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isRoomtypeType() {
    return this.name === TYPE_ID_ROOMTYPE;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isSemiFlexType() {
    return this.name === TYPE_ID_SEMI_FLEX;
  }
}
